<template>
  <div>
    <ChatBubble v-if="!showChat" />
    <div class="resizeable-area">
      <transition name="chat-open">
        <DxResizable
          id="chatContainer"
          :keep-aspect-ratio="false"
          :min-width="minWidth"
          :min-height="minHeight"
          :max-height="maxHeight"
          :max-width="maxWidth"
          handles="left top topleft"
          :class="showChat ? 'borderVisible' : ''"
          v-if="showChat"
          @resize="onChatResize"
        >
          <div class="chat-container" v-show="showChat" :style="typingIndicatorStyle">
            <div class="row chat-options justify-between">
              <span class="chat-title bold-font">{{ t("chat.title") }}</span>
              <div class="chat-icon-options-row row justify-center">
                <i
                  class="dx-icon-copy chat-icon-option"
                  @click="copyEntireChat"
                  id="copyChatIcon"
                  :aria-label="t('chat.copy')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#copyChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.copy") }}
                </DxTooltip>
                <i
                  class="dx-icon-refresh chat-icon-option"
                  @click="refreshChat"
                  id="refreshChatIcon"
                  :aria-label="t('chat.refresh')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#refreshChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.refresh") }}
                </DxTooltip>
                <i
                  class="dx-icon-fullscreen chat-icon-option"
                  @click="increaseChatSize"
                  id="increaseSizeIcon"
                  :aria-label="t('chat.increase_size')"
                  v-if="!isChatMaximized"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#increaseSizeIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                  v-if="!isChatMaximized"
                >
                  {{ t("chat.increase_size") }}
                </DxTooltip>

                <i
                  class="las la-compress la-2x chat-icon-option"
                  @click="decreaseChatSize"
                  id="decreaseSizeIcon"
                  :aria-label="t('chat.decrease_size')"
                  v-if="isChatMaximized"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#decreaseSizeIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                  v-if="isChatMaximized"
                >
                  {{ t("chat.decrease_size") }}
                </DxTooltip>

                <i
                  class="las la-2x la-history chat-icon-option"
                  @click="showHistoryPopup"
                  id="showHistoryBtn"
                  :aria-label="t('chat.chat_history')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#showHistoryBtn"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.chat_history") }}
                </DxTooltip>

                <i
                  class="dx-icon-close chat-icon-option"
                  @click="closeChat"
                  id="closeChatIcon"
                  :aria-label="t('chat.close')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#closeChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.close") }}
                </DxTooltip>
              </div>
            </div>

            <div
              :id="chatUploadDropZoneId"
              ref="chatBoxRef"
              :aria-label="t('chat.upload_drop_zone')"
              class="chat-box"
              :class="{ 'chat-box-drop-active': isDropZoneActive }"
              @drop.capture="handleChatDropCapture"
            >
              <div
                v-if="isDropZoneActive"
                class="chat-drop-overlay"
                aria-hidden="true"
              >
                <i class="dx-icon-upload chat-drop-icon"></i>
                <span>{{ t("chat.drop_file_to_attach") }}</span>
              </div>
              <DxChat
                ref="dxChatRef"
                :data-source="chatDataSource"
                :reload-on-change="false"
                :user="currentUser"
                :showAvatar="true"
                :showUserName="true"
                :showDayHeaders="true"
                :showMessageTimestamp="true"
                :dayHeaderFormat="'dd/MM/yyyy'"
                :messageTimestampFormat="'hh:mm a'"
                message-template="message"
                @message-entered="onMessageEntered"
                :typingUsers="typingUsers"
                class="chat-content"
                @attachment-download-click="onAttachmentDownloadClick"
                ><template #message="{ data }">
                  <div class="chat-message-wrapper">
                    <div
                      v-if="data.message?.text"
                      class="chat-message-text"
                      v-html="renderMessage(data.message)"
                    ></div>

                    <div class="message-actions">
                      <div
                          v-if="isAssistantMessage(data)"
                          class="message-feedback"
                        >
                          <!-- Real buttons (WCAG 2.1.1 / 4.1.2): keyboard-operable, named and with a pressed state.
                               The saved thumb stays enabled so its active look is kept, but announces
                               aria-disabled because it can't be changed; the other thumb is disabled.
                               PBI 11215: a saved thumbs down without details stays operable and opens the
                               feedback panel below (aria-expanded / aria-controls); it never re-sends the rating. -->
                          <button
                            type="button"
                            class="feedback-icon"
                            :class="{ active: isThumbsUp(data.message) }"
                            :title="t('chat.helpful')"
                            :aria-label="t('chat.helpful')"
                            :aria-pressed="isThumbsUp(data.message) ? 'true' : 'false'"
                            :aria-disabled="isThumbsUp(data.message) ? 'true' : undefined"
                            :disabled="isMessageStreaming(data.message) || (hasFeedback(data.message) && !isThumbsUp(data.message))"
                            @click="submitFeedback(data.message, EMessageRating.ThumbsUp)"
                          >
                            <i class="fas fa-thumbs-up" aria-hidden="true"></i>
                          </button>

                          <button
                            :id="thumbsDownButtonId(data.message)"
                            type="button"
                            class="feedback-icon"
                            :class="{ active: isThumbsDown(data.message) }"
                            :title="t('chat.not_helpful')"
                            :aria-label="t('chat.not_helpful')"
                            :aria-pressed="isThumbsDown(data.message) ? 'true' : 'false'"
                            :aria-disabled="isThumbsDown(data.message) && !canOpenDetails(data.message) ? 'true' : undefined"
                            :aria-expanded="canOpenDetails(data.message) ? String(isDetailsPanelOpen(data.message)) : undefined"
                            :aria-controls="isDetailsPanelOpen(data.message) ? detailsPanelId(data.message) : undefined"
                            :disabled="isMessageStreaming(data.message) || (hasFeedback(data.message) && !isThumbsDown(data.message))"
                            @click="onThumbsDownClick(data.message)"
                          >
                            <i class="fas fa-thumbs-down feedback-icon-down" aria-hidden="true"></i>
                          </button>

                          <i
                            :class="
                              copiedMessageId === data.message.id
                                ? 'dx-icon-check feedback-copy-icon'
                                : 'dx-icon-copy feedback-copy-icon'
                            "
                            @click="
                              copyMessageWithFeedback(
                                data.message.id,
                                renderMessage(data.message),
                              )
                            "
                            :title="
                              copiedMessageId === data.message.id
                                ? t('chat.copied')
                                : t('chat.copy_message')
                            "
                          ></i>

                          <span class="feedback-confirmation" role="status">
                            {{
                              isDetailsPanelOpen(data.message)
                                ? t("chat.feedback_more")
                                : hasFeedback(data.message)
                                  ? t("chat.feedback_thanks")
                                  : t("chat.was_this_helpful")
                            }}
                          </span>
                        </div>

                        <!-- PBI 11215: inline, non-modal feedback panel below the thumbs-downed answer. The chat stays
                             usable (no focus trap); Escape or Skip closes it without any request. -->
                        <div
                          v-if="isAssistantMessage(data) && isDetailsPanelOpen(data.message)"
                          :id="detailsPanelId(data.message)"
                          class="feedback-panel"
                          role="group"
                          :aria-labelledby="`${detailsPanelId(data.message)}-title`"
                          @keydown.esc.stop.prevent="skipDetails(data.message)"
                        >
                          <p :id="`${detailsPanelId(data.message)}-title`" class="feedback-panel-title">
                            {{ t("chat.feedback_question") }}
                          </p>

                          <!-- The reasons and their labels come from the server (flag value id, label description);
                               without them (failed load) the panel offers the comment only. -->
                          <div v-if="thumbsDownReasonOptions.length > 0" class="feedback-reason-list">
                            <!-- Multi-select toggle buttons: aria-pressed plus a check mark, so the selected state
                                 is not conveyed by colour alone (WCAG 1.4.1 / 4.1.2). -->
                            <button
                              v-for="reason in thumbsDownReasonOptions"
                              :key="reason.id"
                              type="button"
                              class="feedback-reason"
                              :class="{ selected: isReasonSelected(reason.id) }"
                              :aria-pressed="isReasonSelected(reason.id) ? 'true' : 'false'"
                              :aria-disabled="detailsPanel.saving ? 'true' : undefined"
                              @click="toggleReason(reason.id)"
                            >
                              <i
                                v-if="isReasonSelected(reason.id)"
                                class="fas fa-check feedback-reason-check"
                                aria-hidden="true"
                              ></i>
                              {{ reason.description }}
                            </button>
                          </div>

                          <label
                            :for="`${detailsPanelId(data.message)}-comment`"
                            class="feedback-comment-label"
                          >
                            {{ t("chat.feedback_comment_label") }}
                          </label>
                          <textarea
                            :id="`${detailsPanelId(data.message)}-comment`"
                            v-model="detailsPanel.comment"
                            class="feedback-comment"
                            rows="2"
                            :maxlength="MAX_FEEDBACK_COMMENT_LENGTH"
                            :aria-describedby="`${detailsPanelId(data.message)}-counter`"
                            :readonly="detailsPanel.saving"
                          ></textarea>
                          <p :id="`${detailsPanelId(data.message)}-counter`" class="feedback-comment-counter">
                            {{
                              t("chat.feedback_comment_counter", {
                                count: detailsPanel.comment.length,
                                max: MAX_FEEDBACK_COMMENT_LENGTH,
                              })
                            }}
                          </p>

                          <p v-if="detailsPanel.error" class="feedback-panel-error" role="alert">
                            {{ detailsPanel.error }}
                          </p>

                          <div class="feedback-actions">
                            <!-- aria-disabled instead of disabled keeps Send focusable, so its hint is reachable. -->
                            <button
                              type="button"
                              class="feedback-send"
                              :aria-disabled="canSendDetails ? undefined : 'true'"
                              :aria-describedby="hasDetailsInput ? undefined : `${detailsPanelId(data.message)}-hint`"
                              @click="sendDetails(data.message)"
                            >
                              {{ t("chat.send_feedback") }}
                            </button>
                            <button
                              type="button"
                              class="feedback-skip"
                              :aria-disabled="detailsPanel.saving ? 'true' : undefined"
                              @click="skipDetails(data.message)"
                            >
                              {{ t("chat.skip") }}
                            </button>
                          </div>
                          <p
                            v-if="!hasDetailsInput"
                            :id="`${detailsPanelId(data.message)}-hint`"
                            class="feedback-send-hint"
                          >
                            {{ t("chat.feedback_send_hint") }}
                          </p>
                        </div>
                      </div>
                  </div>
                </template>
                <DxFileUploaderOptions
                  :accept="acceptedAttachmentExtensions"
                  :allowed-file-extensions="allowedAttachmentExtensions"
                  drop-zone="#chatUploadDropZone"
                  :multiple="false"
                  :on-drop-zone-enter="handleDropZoneEnter"
                  :on-drop-zone-leave="clearDropZoneActive"
                  :on-upload-error="handleUploadError"
                  :upload-file="uploadFile"
                />
              </DxChat>
            </div>
          </div>
        </DxResizable>
      </transition>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import store from "../../store/index";
import { useI18n } from "vue-i18n";
import ChatBubble from "./ChatBubble.vue";
import { DxChat, DxFileUploaderOptions } from "devextreme-vue/chat";
import { DxTooltip } from "devextreme-vue/tooltip";
import DxResizable from "devextreme-vue/resizable";
import moment from "moment";
import AiAssistantService from "../../services/aiAssistant.service";
import MarkdownIt from "markdown-it";
import createChatStream from "../../services/sse.service";
import EMessageRating from "../../enums/EMessageRating";
import EThumbsDownReason from "../../enums/EThumbsDownReason";
import {
  chatDataSource,
  reloadChatDataSource,
  syncChatDataSource,
} from "../../store/modules/custom.store";
import { useDelayedRenderer } from "../../helpers/delayedRenderer";
import { Guid } from "devextreme-vue/common";
import aiAssistantService from "../../services/aiAssistant.service";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

const { t, locale } = useI18n();
const typingUsers = computed(() => store.state.chat.typingUsers);
// Feeds the `--chat-typing-text` CSS variable that overrides the typing-indicator
// text (see _chat.scss). Sourced from the store so it matches the locale-resolved
// assistant name (Vinny for Dutch, Lotse for German).
const typingIndicatorStyle = computed(() => ({
  "--chat-typing-text": `'${store.state.chat.supportAgent.name} is thinking...'`,
}));
const noResultsText = t("chat.no_results");
const showChat = computed({
  get: () => store.state.chat.showChat,
});
const renderMessage = (message) => {
  if (!message || !message.text) return "";

  return md.render(message.text);
};

const maxHeight = ref(0);
const maxWidth = ref(0);
const isStreaming = ref(false);
const activeStream = ref(null);
const activeRenderer = ref(null);
const isStoppingStream = ref(false);
const chatBoxRef = ref(null);
const dxChatRef = ref(null);
const pendingAttachments = ref([]);
const defaultAllowedAttachmentExtensions = [".doc", ".docx", ".pdf"];
const allowedAttachmentExtensions = ref([...defaultAllowedAttachmentExtensions]);
const chatUploadDropZoneId = "chatUploadDropZone";
const isDropZoneActive = ref(false);
const acceptedAttachmentExtensions = computed(() =>
  allowedAttachmentExtensions.value.join(","),
);
const isAssistantMessage = (data) => {
  return data?.message?.author?.id === store.state.chat.supportAgent.id && !data?.message?.isWelcomeMessage;
};

const pushChatChanges = (changes) => {
  syncChatDataSource(changes);
};

const syncInsertedMessage = (message) => {
  pushChatChanges([{ type: "insert", data: message }]);
};

const syncUpdatedMessage = (messageId, data) => {
  pushChatChanges([{ type: "update", key: messageId, data }]);
};

const syncRemovedMessage = (messageId) => {
  pushChatChanges([{ type: "remove", key: messageId }]);
};

const getChatRootElement = () =>
  dxChatRef.value?.$el ?? chatBoxRef.value?.$el ?? chatBoxRef.value ?? null;

const getScrollContainer = () => {
  const root = getChatRootElement();
  if (!root) return null;

  const candidates = [
    root.querySelector(".dx-scrollable-container"),
    root.querySelector(".dx-scrollview-content")?.parentElement ?? null,
    root.querySelector(".dx-chat-message-list"),
  ];

  return candidates.find((candidate) => candidate instanceof HTMLElement) ?? null;
};

let isScrollScheduled = false;
const scrollToBottom = async () => {
  // Coalesce: many stream updates in one frame should trigger only one scroll pass.
  if (isScrollScheduled) return;
  isScrollScheduled = true;

  await nextTick();

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      isScrollScheduled = false;
      const scrollContainer = getScrollContainer();
      if (!scrollContainer) return;

      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    });
  });
};

const finalizeStreamingState = () => {
  activeRenderer.value = null;
  activeStream.value = null;
  store.dispatch("chat/clearStreamingState");
  isStoppingStream.value = false;
  isStreaming.value = false;
};

// assistantId is the server's assistantMessageId from the stream events, the id the answer is stored under in ES
// (feedback is given on that id). The fallback only keeps the chat item keyed if an event ever lacks it.
const insertAssistantPlaceholder = async (assistantId) => {
  const message = await store.dispatch(
    "chat/insertAssistantPlaceholder",
    assistantId || new Guid().toString(),
  );
  syncInsertedMessage(message);
  scrollToBottom();
  return message;
};

const ensureAssistantSession = async () => {
  if (
    !store.state.chat.aiAssistantAccessToken.accessToken ||
    moment(store.state.chat.aiAssistantAccessToken.expirationTime).isBefore(
      moment().add(1, "minute"),
    )
  ) {
    const response = await AiAssistantService.getAIAssistantAccessToken();

    if (response.status === 200) {
      await store.dispatch(
        "chat/setAIAssistantAccessToken",
        response.data,
        t("chat.error_occurred"),
      );
    }
  }
};

const stopStreaming = () => {
  activeRenderer.value?.stop();
  if (activeStream.value) {
    isStoppingStream.value = true;
    activeStream.value.close();
    return;
  }

  finalizeStreamingState();
};

const updateLastMessageText = (assistantMessageId, text) => {
  if (!assistantMessageId) return;

  store.dispatch("chat/updateAssistantMessageText", {
    assistantMessageId,
    text,
  });
  syncUpdatedMessage(assistantMessageId, { text });
  scrollToBottom();
};

const appendGeneratedFile = (assistantMessageId, file) => {
  if (!assistantMessageId || !file) return;

  store.dispatch("chat/addAssistantGeneratedFile", { assistantMessageId, file });

  const updatedMessage = (store.getters["chat/visibleMessages"] ?? []).find(
    (message) => message.id === assistantMessageId,
  );

  syncUpdatedMessage(assistantMessageId, {
    attachments: updatedMessage?.attachments ?? [],
  });
  scrollToBottom();
};

const removeMessage = (messageId) => {
  if (!messageId) return;

  store.dispatch("chat/removeMessageById", messageId);
  syncRemovedMessage(messageId);
};

const updateViewportLimits = () => {
  maxWidth.value = window.innerWidth * 0.9;
  maxHeight.value = window.innerHeight * 0.9;
};

const minWidth = computed(() => {
  if (window.innerWidth < 820) {
    return window.innerWidth * 0.9;
  }
  return 720;
});

const minHeight = computed(() => {
  if (window.innerHeight < 820) {
    return window.innerHeight * 0.9;
  }
  return 620;
});

onMounted(() => {
  updateViewportLimits();
  window.addEventListener("resize", updateViewportLimits);
  scrollToBottom();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewportLimits);
  stopStreaming();
});

const refreshChat = async () => {
  stopStreaming();
  pendingAttachments.value = [];
  store.dispatch("chat/resetChat");
  store.dispatch("chat/startNewConversation");
  await reloadChatDataSource();
  scrollToBottom();
};

const closeChat = () => {
  store.commit("chat/updateShowChat", false);
};

const isChatMaximized = ref(false);

const onChatResize = (e) => {
  if (e.width >= maxWidth.value - 10 && e.height >= maxHeight.value - 10)
    isChatMaximized.value = true;
  else isChatMaximized.value = false;
};

const decreaseChatSize = () => {
  const el = document.getElementById("chatContainer");
  if (!el) return;

  el.classList.add("animate-resize");

  el.style.width = `${minWidth.value}px`;
  el.style.height = `${minHeight.value}px`;
  isChatMaximized.value = false;

  setTimeout(() => {
    el.classList.remove("animate-resize");
  }, 200);
};

const increaseChatSize = () => {
  const el = document.getElementById("chatContainer");
  if (!el) return;
  /*
  Added this class (animate-resize) to do a simple animation when clicking button
  to increase size. Needs to be removed because if always kept, it messes with
  the resizing of chat when dragging,
  */
  el.classList.add("animate-resize");

  el.style.width = `${maxWidth.value}px`;
  el.style.height = `${maxHeight.value}px`;
  isChatMaximized.value = true;
  setTimeout(() => {
    el.classList.remove("animate-resize");
  }, 200);
};

onBeforeMount(async () => {
  const initializationTasks = [loadAttachmentSettings()];

  if (store.state.chat.settings === null)
    initializationTasks.push(AiAssistantService.getAISettings());

  // Not awaited: the chat starts without them; a failed load stays null and is retried when a panel opens.
  if (store.state.chat.thumbsDownReasonOptions === null)
    AiAssistantService.getThumbsDownReasons();

  await Promise.all(initializationTasks);
  await store.dispatch("chat/initChatFromSession");
  // The data source is a module level singleton, so it can already hold the items
  // of an earlier mount. Reload it from the store now that the conversation is
  // restored, otherwise DxChat renders whatever was cached before.
  await reloadChatDataSource();
});
const currentUser = computed({
  get: () => store.state.chat.currentUser,
});

const createUserMessage = (text, attachments = [], attachmentIds = []) => {
  const message = {
    id: new Guid().toString(),
    timestamp: new Date(),
    author: store.state.chat.currentUser,
    text,
    conversationId: store.state.chat.currentConversationId,
  };

  if (attachments.length) {
    message.attachments = attachments.map((attachment) => ({
      attachmentId: attachment.attachmentId,
      name: attachment.name,
      size: attachment.size,
    }));
  }

  if (attachmentIds.length) {
    message.attachmentIds = [...attachmentIds];
  }

  return message;
};

const normalizeAttachment = (attachment) => ({
  attachmentId: attachment.attachmentId,
  name: attachment.name,
  size: attachment.size,
});

const resolveMessageAttachments = (enteredMessageAttachments = []) => {
  const availableAttachments = [...pendingAttachments.value];
  const attachmentCandidates = enteredMessageAttachments.length
    ? enteredMessageAttachments
    : availableAttachments;

  const resolvedAttachments = [];
  const resolvedAttachmentIds = [];

  attachmentCandidates.forEach((attachment) => {
    const matchIndex = availableAttachments.findIndex(
      (candidate) =>
        candidate.name === attachment.name && candidate.size === attachment.size,
    );

    if (matchIndex === -1) {
      return;
    }

    const [matchedAttachment] = availableAttachments.splice(matchIndex, 1);
    resolvedAttachments.push(normalizeAttachment(matchedAttachment));
    resolvedAttachmentIds.push(matchedAttachment.attachmentId);
  });

  return {
    attachments: resolvedAttachments,
    attachmentIds: resolvedAttachmentIds,
  };
};

const insertUserMessage = (message) => {
  store.dispatch("chat/insertUserMessage", message);
  syncInsertedMessage(message);
  scrollToBottom();
};

const syncCurrentConversationWelcomeMessage = () => {
  const messages = currentConversationMessages.value ?? [];
  const welcomeMessage = messages[messages.length - 1];

  if (!welcomeMessage) return;

  syncInsertedMessage(welcomeMessage);
};

const currentConversationMessages = computed(() => {
  const currentConversationId = store.state.chat.currentConversationId;
  return currentConversationId
    ? store.state.chat.messagesByConversation[currentConversationId] ?? []
    : [];
});

const onMessageEntered = async (e) => {
  const message = e?.message?.text ?? e?.text ?? "";

  const enteredMessageAttachments = Array.isArray(e?.message?.attachments)
    ? e.message.attachments
    : [];
    
  if (!message && enteredMessageAttachments.length === 0) return;
  const { attachments, attachmentIds } = resolveMessageAttachments(
    enteredMessageAttachments,
  );
  const userMessage = createUserMessage(message, attachments, attachmentIds);
  pendingAttachments.value = [];
  insertUserMessage(userMessage);
  const conversationSnapshot = [...currentConversationMessages.value];

  isStreaming.value = true;

  const requestStreamId = async () => {
    await ensureAssistantSession();

    const streamId = await AiAssistantService.processPrompt(
      conversationSnapshot,
      locale.value
    );

    if (!streamId) {
      throw new Error(t("chat.error_occurred"));
    }

    store.commit("chat/setStreamId", streamId);
    return streamId;
  };

  try {
    const streamId = await requestStreamId();
    let buffer = "";
    let assistantMessage = null;
    let assistantMessagePromise = null;
    let pendingFiles = [];

    const ensureAssistantMessage = async (assistantMessageId) => {
      if (assistantMessage) return assistantMessage;

      if (!assistantMessagePromise) {
        assistantMessagePromise = insertAssistantPlaceholder(assistantMessageId).then((message) => {
          assistantMessage = message;
          activeRenderer.value = useDelayedRenderer((chunk) => {
            buffer += chunk;
            updateLastMessageText(assistantMessage.id, buffer);
          });

          return message;
        });
      }

      return assistantMessagePromise;
    };

    if (activeStream.value) activeStream.value.close();
    activeStream.value = createChatStream({
      streamId,
      onMessage: async (chunk, messageId) => {
        const message = await ensureAssistantMessage(messageId);
        activeRenderer.value?.pushChunk(chunk);
        return message;
      },
      onFile: (file) => {
        if (!file) return;
        // Buffer only — do NOT create the placeholder here. The file event arrives before the
        // model's summary text, so creating the bubble now would leave it empty (and visible)
        // during that latency. The placeholder is created on the first text delta; the chip is
        // attached in onComplete so the message reads "text, then file".
        pendingFiles.push(file);
      },
      onError: async (streamError) => {
        const errorText = typeof streamError === "string" && streamError
          ? streamError
          : noResultsText;

        activeRenderer.value?.stop();
        if (assistantMessage) {
          updateLastMessageText(assistantMessage.id, errorText);
        } else {
          await store.dispatch("chat/addAssistantMessage", errorText);
          syncInsertedMessage(
            currentConversationMessages.value[
              currentConversationMessages.value.length - 1
            ],
          );
        }
        finalizeStreamingState();
        store.dispatch("chat/startNewConversation");
        syncCurrentConversationWelcomeMessage();
        scrollToBottom();
      },
      onAbort: () => {
        activeRenderer.value?.stop();
        if (!buffer && assistantMessage) {
          removeMessage(assistantMessage.id);
        }

        if (isStoppingStream.value) {
          finalizeStreamingState();
          return;
        }

        finalizeStreamingState();
      },
      onComplete: async ({ empty, messageId }) => {
        activeRenderer.value?.stop({ flush: true });

        const hasFiles = pendingFiles.length > 0;

        if (empty && !hasFiles) {
          if (assistantMessage) {
            updateLastMessageText(assistantMessage.id, noResultsText);
          } else {
            await store.dispatch("chat/addAssistantMessage", noResultsText);
            syncInsertedMessage(
              currentConversationMessages.value[
                currentConversationMessages.value.length - 1
              ],
            );
          }
        }

        // Render the generated-file chip(s) now, after the text — so the message shows
        // "text, then file". The file is already stored server-side, so this is instant.
        // ensureAssistantMessage also covers the rare "file but no text" case.
        if (hasFiles) {
          const message = await ensureAssistantMessage(messageId);
          pendingFiles.forEach((file) => appendGeneratedFile(message.id, file));
          pendingFiles = [];
        }

        // Langdock sends the unique message when moderation error occurs on their side, so a new conversation has to be started in that case
        if (buffer && buffer === t('chat.error_occurred')) {
          store.dispatch("chat/startNewConversation");
          syncCurrentConversationWelcomeMessage();
        }

        finalizeStreamingState();
        scrollToBottom();
      },
    });
  } catch (error) {
    activeRenderer.value?.stop();
    store.dispatch("chat/addAssistantMessage", t("chat.error_occurred"));
    syncInsertedMessage(
      currentConversationMessages.value[currentConversationMessages.value.length - 1],
    );
    finalizeStreamingState();
    store.dispatch("chat/startNewConversation");
    syncCurrentConversationWelcomeMessage();
    scrollToBottom();
  }
};

const showHistoryPopup = () => {
  store.commit("chat/updateIsHistoryPopupVisible", true);
  closeChat();
};

const stripHtml = (html) => {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.innerText;
};
const copiedMessageId = ref(null);

// Feedback is written once per message by the server; the saved state lives on the message in the chat store
// (so it survives a chat remount) and is mirrored into the DxChat data source. The template reads it from the
// reactive store message — the same `rating` the ES history load fills in — not from the DxChat item: DxChat
// only re-renders a bubble when its text changes, so a rating merged into the item would not repaint until reload.
const storedMessagesById = computed(
  () =>
    new Map(
      (store.getters["chat/visibleMessages"] ?? []).map((message) => [
        message.id,
        message,
      ]),
    ),
);

const feedbackState = (message) =>
  storedMessagesById.value.get(message?.id) ?? message;

const isThumbsUp = (message) =>
  feedbackState(message)?.rating === EMessageRating.ThumbsUp;

const isThumbsDown = (message) =>
  feedbackState(message)?.rating === EMessageRating.ThumbsDown;

const hasFeedback = (message) => {
  const state = feedbackState(message);

  return (
    (state?.rating ?? EMessageRating.None) !== EMessageRating.None ||
    state?.feedbackLocked === true
  );
};

// PBI 11470: the answer still being streamed is the one whose placeholder the stream inserted (assistantMessageId,
// cleared by finalizeStreamingState on complete, error, abort and stop); its thumbs stay disabled until then.
const isMessageStreaming = (message) =>
  Boolean(message?.id) && store.state.chat.assistantMessageId === message.id;

// Guards against a second click while the first save is still in flight.
const feedbackInFlight = new Set();

const applyMessageFeedback = (messageId, feedback) => {
  store.dispatch("chat/setMessageFeedback", { messageId, ...feedback });

  const changes = { feedbackLocked: Boolean(feedback.feedbackLocked) };
  if (feedback.rating !== undefined) changes.rating = feedback.rating;

  syncUpdatedMessage(messageId, changes);
};

// Thumbs up and thumbs down are saved the same way, once per message (PBI 11211). The reasons and the comment of a
// thumbs down are added to that same saved feedback later by the feedback panel (PBI 11215).
// Resolves true once the rating is saved (204).
const submitFeedback = async (message, rating) => {
  if (rating !== EMessageRating.ThumbsUp && rating !== EMessageRating.ThumbsDown) return false;

  // PBI 11470: no feedback on an answer that is still streaming (its buttons are disabled then as well).
  if (isMessageStreaming(message)) return false;

  if (!message?.id || hasFeedback(message) || feedbackInFlight.has(message.id)) return false;

  feedbackInFlight.add(message.id);

  try {
    await ensureAssistantSession();

    // The message's own conversation id, not the current conversation.
    await aiAssistantService.updateMessageRating(
      message.id,
      message.conversationId,
      rating,
    );

    applyMessageFeedback(message.id, { rating, feedbackLocked: false });
    return true;
  } catch (error) {
    if (error?.response?.status === 409) {
      // The message already has feedback (a double click, another tab): lock both buttons. The stored
      // rating is shown after the next history load.
      applyMessageFeedback(message.id, { feedbackLocked: true });
      return false;
    }

    console.error("Failed to save message rating", error);
    return false;
  } finally {
    feedbackInFlight.delete(message.id);
  }
};

// --- Thumbs-down feedback panel (PBI 11215) ----------------------------------------------------------------------
// The reasons and the comment are added to the saved thumbs down once, through their own endpoint; the rating is
// never sent again. Only one panel is open at a time; its state lives here (not on the DxChat item) and is not kept
// across a remount or refresh. The saved details come from the store message, like the rating.

// Same cap as the server and the SQL column (nvarchar(1000)); counted in UTF-16 units like maxlength.
const MAX_FEEDBACK_COMMENT_LENGTH = 1000;

// Loaded once from the server (EnumsService.GetThumbsDownReasons, labels via GetDisplayNameOrDescription).
const thumbsDownReasonOptions = computed(() => store.state.chat.thumbsDownReasonOptions ?? []);

const closedDetailsPanel = () => ({
  messageId: null,
  reasons: EThumbsDownReason.None,
  comment: "",
  saving: false,
  error: "",
});

const detailsPanel = ref(closedDetailsPanel());

const detailsPanelId = (message) => `feedback-panel-${message?.id}`;
const thumbsDownButtonId = (message) => `feedback-down-${message?.id}`;

const isDetailsPanelOpen = (message) =>
  Boolean(message?.id) && detailsPanel.value.messageId === message.id;

const hasDetails = (message) => {
  const state = feedbackState(message);

  return (
    (state?.thumbsDownReason ?? EThumbsDownReason.None) !== EThumbsDownReason.None ||
    (state?.comment ?? null) !== null
  );
};

// A saved thumbs down whose details are still empty (the user skipped, or refreshed) may reopen the panel locally.
// Once details are saved — or the server reports them (409) — they are final.
const canOpenDetails = (message) => {
  const state = feedbackState(message);

  return (
    isThumbsDown(message) &&
    !hasDetails(message) &&
    state?.detailsLocked !== true &&
    state?.feedbackLocked !== true
  );
};

const hasDetailsInput = computed(
  () =>
    detailsPanel.value.reasons !== EThumbsDownReason.None ||
    detailsPanel.value.comment.trim() !== "",
);

const canSendDetails = computed(
  () =>
    hasDetailsInput.value &&
    !detailsPanel.value.saving &&
    feedbackState({ id: detailsPanel.value.messageId })?.detailsLocked !== true,
);

const isReasonSelected = (reason) => (detailsPanel.value.reasons & reason) === reason;

// Multi-select: the selected reasons are combined with bitwise OR into one EThumbsDownReason value.
const toggleReason = (reason) => {
  if (detailsPanel.value.saving) return;

  detailsPanel.value.reasons = isReasonSelected(reason)
    ? detailsPanel.value.reasons & ~reason
    : detailsPanel.value.reasons | reason;
};

const focusAfterRender = (elementId, selector) => {
  nextTick(() => {
    window.requestAnimationFrame(() => {
      const element = document.getElementById(elementId);
      const target = selector ? element?.querySelector(selector) : element;

      element?.scrollIntoView({ block: "nearest" });
      target?.focus();
    });
  });
};

const openDetailsPanel = (message) => {
  detailsPanel.value = { ...closedDetailsPanel(), messageId: message.id };

  // Reasons not loaded yet: joins the request still running from the chat start, or retries once after it failed;
  // the panel opens with the comment meanwhile.
  if (store.state.chat.thumbsDownReasonOptions === null)
    AiAssistantService.getThumbsDownReasons();

  // Focus moves into the panel (to the first reason, or the comment without reasons); it is not a dialog, so
  // nothing is trapped.
  focusAfterRender(detailsPanelId(message), ".feedback-reason, .feedback-comment");
};

const closeDetailsPanel = (message) => {
  detailsPanel.value = closedDetailsPanel();

  // Back to the thumbs down, so keyboard focus is never lost to the page (WCAG 2.4.3).
  focusAfterRender(thumbsDownButtonId(message));
};

const onThumbsDownClick = async (message) => {
  if (!message?.id) return;

  if (canOpenDetails(message)) {
    // Already saved: open or close the panel locally, never send the rating again.
    if (isDetailsPanelOpen(message)) closeDetailsPanel(message);
    else openDetailsPanel(message);
    return;
  }

  if (await submitFeedback(message, EMessageRating.ThumbsDown)) openDetailsPanel(message);
};

// Skip is the no-details path: close the panel, keep the saved thumbs down, send nothing.
const skipDetails = (message) => {
  if (detailsPanel.value.saving) return;

  closeDetailsPanel(message);
};

const sendDetails = async (message) => {
  if (!canSendDetails.value || !isDetailsPanelOpen(message)) return;

  // Same normalization as the server: trimmed, an empty comment is null.
  const thumbsDownReason = detailsPanel.value.reasons;
  const comment = detailsPanel.value.comment.trim() || null;

  detailsPanel.value.saving = true;
  detailsPanel.value.error = "";

  try {
    await ensureAssistantSession();

    await aiAssistantService.updateMessageFeedbackDetails(
      message.id,
      message.conversationId,
      thumbsDownReason,
      comment,
    );

    store.dispatch("chat/setMessageFeedback", { messageId: message.id, thumbsDownReason, comment });
    syncUpdatedMessage(message.id, { thumbsDownReason, comment });

    closeDetailsPanel(message);
  } catch (error) {
    if (!isDetailsPanelOpen(message)) return;

    if (error?.response?.status === 409) {
      // Other details were saved already (another tab): they are final, so Send stays disabled here.
      store.dispatch("chat/setMessageFeedback", { messageId: message.id, detailsLocked: true });
      detailsPanel.value.error = t("chat.feedback_details_already_sent");
      return;
    }

    // Keep the draft so the user can try again.
    console.error("Failed to save thumbs-down details", error);
    detailsPanel.value.error = t("chat.feedback_details_error");
  } finally {
    if (isDetailsPanelOpen(message)) detailsPanel.value.saving = false;
  }
};

const copyMessageWithFeedback = async (id, htmlContent) => {
  const textContent = stripHtml(htmlContent);

  copiedMessageId.value = id;

  setTimeout(() => {
    copiedMessageId.value = null;
  }, 2000);

  try {
    if (navigator.clipboard && window.ClipboardItem) {
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const textBlob = new Blob([textContent], { type: "text/plain" });

      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob,
        }),
      ]);

      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(textContent);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = textContent;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);

    textarea.focus();
    textarea.select();
    document.execCommand("copy");

    document.body.removeChild(textarea);
  } catch (copyError) {
    store.commit("user/updateModalError", t("chat.copy_failed")),
      store.commit("user/enableModalError");
  }
};

const copyEntireChat = async () => {
  if (!chatBoxRef.value) return;

  const html = chatBoxRef.value.innerHTML;
  const text = chatBoxRef.value.innerText;

  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  } catch (err) {
    store.commit("user/updateModalError", t("chat.copy_failed")),
      store.commit("user/enableModalError");
  }
};

const getUploadErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData) {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  if (responseData?.Message) {
    return responseData.Message;
  }

  if (typeof error?.message === "string" && error.message) {
    return error.message;
  }

  return t("chat.error_occurred");
};

const handleUploadError = (event) => {
  event.message = getUploadErrorMessage(event?.error);
};

const getNativeDragEvent = (event) => event?.originalEvent ?? event;

const getDraggedFileCount = (event) => {
  const nativeEvent = getNativeDragEvent(event);
  const dataTransfer = nativeEvent?.dataTransfer;

  if (!dataTransfer) return 0;
  if (dataTransfer.files?.length) return dataTransfer.files.length;
  if (!dataTransfer.items?.length) return 0;

  let fileCount = 0;

  for (let index = 0; index < dataTransfer.items.length; index += 1) {
    if (dataTransfer.items[index].kind === "file") fileCount += 1;
  }

  return fileCount;
};

const isChatDropZoneEvent = (event) => {
  const dropZoneElement = event?.dropZoneElement;

  return (
    dropZoneElement?.id === chatUploadDropZoneId ||
    dropZoneElement?.get?.(0)?.id === chatUploadDropZoneId
  );
};

const showSingleAttachmentError = () => {
  store.commit("user/updateModalError", t("chat.single_file_upload_only"));
  store.commit("user/enableModalError");
};

const clearDropZoneActive = () => {
  isDropZoneActive.value = false;
};

const handleDropZoneEnter = (event) => {
  if (!isChatDropZoneEvent(event)) return;

  const draggedFileCount = getDraggedFileCount(event?.event);
  isDropZoneActive.value = draggedFileCount <= 1;
};

const handleChatDropCapture = (event) => {
  const droppedFileCount = getDraggedFileCount(event);

  clearDropZoneActive();

  if (droppedFileCount <= 1) return;

  event.preventDefault();
  event.stopPropagation();
  showSingleAttachmentError();
};

const loadAttachmentSettings = async () => {
  try {
    const attachmentSettings = await AiAssistantService.getAttachmentSettings();
    const configuredExtensions =
      attachmentSettings?.allowedAttachmentExtensions;

    if (Array.isArray(configuredExtensions) && configuredExtensions.length) {
      allowedAttachmentExtensions.value = configuredExtensions;
    }
  } catch (error) {
    allowedAttachmentExtensions.value = [...defaultAllowedAttachmentExtensions];
  }
};

/* Enable when the DevExtreme v26.1 is available. Use the :send-button-options attribute on the DxChat component and bind it to the following property
const sendButtonOptions = computed<DxChatTypes.SendButtonProperties>(() =>
  isStreaming.value
    ? { action: "custom", icon: "stopfilled", onClick: stopStreaming }
    : { action: "send", icon: "arrowright", onClick: () => {} }
);*/

const uploadFile = async (file, progressCallback) => {
  try {
    await ensureAssistantSession();

    const formData = new FormData();
    formData.append("file", file);

    const response = await AiAssistantService.uploadAttachment(formData);
    const attachmentId = response?.data?.attachmentId;

    if (!attachmentId) {
      throw new Error(t("chat.error_occurred"));
    }

    pendingAttachments.value = [
      {
        attachmentId: attachmentId,
        name: file.name,
        size: file.size,
      },
    ];

    progressCallback(file.size);

    return response.data;
  } catch (error) {
    throw new Error(getUploadErrorMessage(error));
  }
};

const onAttachmentDownloadClick = async (event) => {
  await ensureAssistantSession();
  await downloadAttachment(event.attachment.attachmentId, event.attachment.name);
};

const downloadAttachment = async (attachmentId, name) => {
  await aiAssistantService.downloadAttachment(attachmentId)
    .then((response) => {
      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], { type: response.data.type })
      );
      let a = document.createElement("a");
      a.setAttribute("style", "display:none;");
      a.download = name;
      a.href = fileURL;
      a.target = "_blank";
      a.click();
    })
    .catch(async (error) => {
      const data = error.response?.data;

      if (data instanceof Blob) {
        const message = await data.text();
        store.commit("user/updateModalError", message);
      } else store.commit("user/updateModalError", error.message);

      store.commit("user/enableModalError");
    });
};
</script>

<style scoped lang="scss">
.options {
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-width: 280px;
  background-color: rgba(191, 191, 191, 0.15);
  gap: 16px;
}

.caption {
  font-size: 14px;
  font-weight: 500;
}

.option-separator {
  border-bottom: 1px solid #0a108f;
}

.dx-avatar {
  border: 1px solid #0a108f;
}

#chatContainer {
  width: 720px;
  height: 620px;
  bottom: 20px;
  right: 20px;
  position: fixed;
  z-index: 999;
  max-height: 90vh;
  max-width: 93vw;
  /*
  Note: Keep this, translate(0,0) will keep chat in fixed position
  if a better solution is found I'll implement it.
  */
  transform: translate(0, 0) !important;
}
#chatContainer.animate-resize {
  transition: width 0.2s ease, height 0.2s ease;
}
.borderVisible {
  border-radius: 14px;
  background: white;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.chat-open-enter-active {
  transition: opacity 0.35s ease-out, transform 0.35s ease-out;
}

.chat-open-enter-from {
  opacity: 0;
  transform: scale(0.96);
}

.chat-open-enter-to {
  opacity: 1;
  transform: scale(1);
}

.chat-open-leave-active {
  transition: none;
}

#chatContainer {
  transform-origin: top left;
}

.chat-box {
  position: relative;
}

.chat-box-drop-active {
  outline: 2px dashed #0a108f;
  outline-offset: -8px;
}

.chat-drop-overlay {
  align-items: center;
  background: rgba(222, 246, 254, 0.92);
  color: #0a108f;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
  inset: 0;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  text-align: center;
  z-index: 2;
}

.chat-drop-icon {
  font-size: 32px;
}

.message-feedback {
  display: flex;
  align-items: center;
  gap: 12px;
}

.feedback-icon,
.feedback-copy-icon {
  color: #8a8a8a;
  cursor: pointer;
  font-size: 18px;
  opacity: 1;
  transition: color 0.15s ease;
}

// The thumbs are buttons now; reset the native button chrome so they look exactly like the former icons.
button.feedback-icon {
  appearance: none;
  background: none;
  border: 0;
  margin: 0;
  padding: 0;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

// Keyboard focus indicator (WCAG 2.4.7); #0a108f is well above 3:1 on the white and #f7f7f7 bubbles.
button.feedback-icon:focus-visible {
  outline: 2px solid #0a108f;
  outline-offset: 2px;
  border-radius: 2px;
}

.feedback-icon-down {
  transform: scaleX(-1);
}

.feedback-icon:hover,
.feedback-copy-icon:hover {
  color: #666;
}

.feedback-icon.active {
  color: #0a108f;
}

// #6b6b6b: 5.0:1 on the #f7f7f7 assistant bubble, 5.3:1 on white (WCAG 1.4.3); #9a9a9a was 2.6:1.
.feedback-confirmation {
  color: #6b6b6b;
  font-size: 13px;
  white-space: nowrap;
}

.feedback-icon:disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: none;
}

// --- Thumbs-down feedback panel (PBI 11215): inline and narrow below the answer, never covering the chat. ---
// Colours on the white panel: #767676 borders 4.5:1 (UI components need 3:1, WCAG 1.4.11), #333 text 12.6:1,
// #6b6b6b hints 5.3:1, #b00020 errors 7.3:1, #0a108f selected/primary well above 4.5:1 (WCAG 1.4.3).
.feedback-panel {
  box-sizing: border-box;
  width: 100%;
  max-width: 440px;
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid #767676;
  border-radius: 8px;
  background: #fff;
  color: #333;
}

.feedback-panel-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
}

.feedback-reason-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// At least 24x24 CSS px (WCAG 2.5.8). The selected state adds a check mark and a thicker border, not only colour.
.feedback-reason {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 4px 12px;
  border: 1px solid #767676;
  border-radius: 14px;
  background: #fff;
  color: #333;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.feedback-reason.selected {
  border-color: #0a108f;
  box-shadow: inset 0 0 0 1px #0a108f;
  background: #eef0fb;
  color: #0a108f;
  font-weight: 600;
}

.feedback-reason-check {
  font-size: 11px;
}

.feedback-comment-label {
  display: block;
  margin: 12px 0 4px;
  font-size: 13px;
}

.feedback-comment {
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  padding: 8px 10px;
  border: 1px solid #767676;
  border-radius: 6px;
  color: #333;
  font: inherit;
  font-size: 13px;
  resize: vertical;
}

.feedback-comment-counter,
.feedback-send-hint {
  margin: 4px 0 0;
  color: #6b6b6b;
  font-size: 12px;
}

.feedback-panel-error {
  margin: 8px 0 0;
  color: #b00020;
  font-size: 13px;
}

.feedback-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.feedback-send,
.feedback-skip {
  min-height: 32px;
  padding: 6px 16px;
  border-radius: 4px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.feedback-send {
  border: 1px solid #0a108f;
  background: #0a108f;
  color: #fff;
}

.feedback-skip {
  border: 1px solid #767676;
  background: #fff;
  color: #333;
}

// aria-disabled keeps the controls focusable (Send's hint stays reachable); clicks are ignored in the handlers.
.feedback-reason[aria-disabled="true"],
.feedback-send[aria-disabled="true"],
.feedback-skip[aria-disabled="true"] {
  opacity: 0.5;
  cursor: not-allowed;
}

// Visible keyboard focus (WCAG 2.4.7), like the thumbs.
.feedback-reason:focus-visible,
.feedback-send:focus-visible,
.feedback-skip:focus-visible,
.feedback-comment:focus-visible {
  outline: 2px solid #0a108f;
  outline-offset: 2px;
}
</style>
